// Beheer › Plannen (blok 8, ontwerp a van editie 3).
//
// Per plan (propositie en jaar) een tabel: wie vult in, welke personen, hoe ver het
// plan is, en een knop "Open plan" (het plan alleen lezen, zoals de AM en de BP het
// hebben) of "Instellen" (het venster waarin beheer kiest wie het plan invult).
// Daaronder "Wijzigingen in {propositie}": de laatste regels van het redactielog, met
// "Alle wijzigingen" voor het volledige statusoverzicht van AM en BP (PlannenStatus).
//
// Het scherm rekent niets: alles komt uit GET /api/beheer/plannen/instellingen
// (contract api/plan-beheer.schema.json). Na "Start plan" staat de keuze vast; de
// server weigert een tweede start (409) en dit scherm toont het dan alleen lezen.

import { useState } from "react";

import { Card } from "../../components/ui";
import type { PlanInstelling, PlanInvullen, PlanPersoon } from "../../contracts";
import { ApiError, api } from "../../lib/api";
import { TAB_EIGEN_PARAMS, zetParams } from "../../lib/adres";
import { useResource } from "../../lib/useResource";
import { Dialoog, Foutmelding, KeuzeKaarten, Knop, Notitie, type Persoon, PersoonChip, PersoonVeld, PlatformStijl } from "../../stijl";
import { color, sectionLabel, size, weight } from "../../tokens";
import { PlannenStatus } from "./PlannenStatus";

const INVULLEN_WOORD: Record<PlanInvullen, string> = {
  am_bp: "AM en BP",
  am: "Alleen AM",
  bp: "Alleen BP",
};

const PARAGRAAF_WOORD = (p: string) => (p === "lijst" ? "lijst van 30" : p);

const EEN_PERSOON =
  "Bij één persoon valt de uitslag weg: bespreekpunten zijn dan de grootste verschillen met de AI-assistent.";

function tijd(op: string | null): string {
  if (!op) return "";
  return new Date(op).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

const sleutel = (p: PlanInstelling) => `${p.propositie}-${p.jaar}`;

/** "1.3 · 6 van 12", of een zin zolang er niets is gebeurd. */
export function standTekst(p: PlanInstelling): string {
  if (!p.gestart) return "Nog niet gestart";
  if (!p.stand.paragraaf) return "Nog niet begonnen";
  return `${p.stand.paragraaf} · ${p.stand.klaar} van ${p.stand.van}`;
}

/** Het plan alleen lezen, zoals de AM en de BP het hebben (de plan-tab met ?lees=). */
export function openPlan(p: PlanInstelling): void {
  const delen: Record<string, string | null> = { tab: "plan", lees: sleutel(p), versie: null };
  for (const n of TAB_EIGEN_PARAMS) if (n !== "lees" && n !== "versie") delen[n] = null;
  zetParams(delen, "push", "tab");
}

export function Plannen() {
  const res = useResource(() => api.planInstellingen(), []);
  const [kies, setKies] = useState<string | null>(null);
  const [instellen, setInstellen] = useState<string | null>(null);
  const [alles, setAlles] = useState(false);
  // Zonder de instellingen (bijvoorbeeld als de server ze nog niet heeft): het
  // statusoverzicht zoals het was, zodat beheer altijd iets heeft.
  if (res.error) return <PlannenStatus />;
  if (!res.data) return <p style={{ color: color.faint, fontSize: size.meta }}>Laden…</p>;
  const plannen = res.data.plannen;
  if (!plannen.length) {
    return <p style={{ color: color.soft, fontSize: size.body }}>Er is nog geen pilotpropositie met een plan.</p>;
  }
  const gekozen =
    plannen.find((p) => sleutel(p) === kies) ?? plannen.find((p) => p.wijzigingen.length > 0) ?? plannen[0]!;
  const dubbelJaar = new Set(plannen.map((p) => p.propositie)).size < plannen.length;
  const naam = (p: PlanInstelling) => `${p.naam}${dubbelJaar ? ` ${p.jaar}` : ""}`;
  const open = plannen.find((p) => sleutel(p) === instellen) ?? null;

  return (
    <PlatformStijl>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 }}>
        <Card style={{ padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: size.body }}>
            <thead>
              <tr style={{ textAlign: "left", color: color.soft, fontSize: size.meta }}>
                <th style={kop}>Propositie</th>
                <th style={kop}>Wie vult in</th>
                <th style={kop}>Personen</th>
                <th style={kop}>Stand</th>
                <th style={kop}>
                  <span className="sr-only">Actie</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {plannen.map((p) => {
                const actief = sleutel(p) === sleutel(gekozen);
                const personen = [p.am, p.bp].filter((x): x is PlanPersoon => !!x);
                return (
                  <tr
                    key={sleutel(p)}
                    data-plan={sleutel(p)}
                    aria-selected={actief}
                    style={{ borderTop: `1px solid ${color.border}`, background: actief ? color.surfaceAlt : "transparent" }}
                  >
                    <td style={cel}>
                      <button
                        type="button"
                        onClick={() => setKies(sleutel(p))}
                        style={{ all: "unset", cursor: "pointer", fontWeight: weight.medium, color: color.ink }}
                      >
                        {naam(p)}
                      </button>
                    </td>
                    <td style={cel}>
                      {p.invullen ? INVULLEN_WOORD[p.invullen] : <span style={{ color: color.faint }}>Nog niet ingesteld</span>}
                    </td>
                    <td style={cel}>
                      {personen.length ? personen.map((x) => x.naam).join(", ") : <span style={{ color: color.faint }}>–</span>}
                      {p.meelezers.length > 0 && (
                        <span style={{ color: color.soft, fontSize: size.meta }}>
                          {" "}
                          · {p.meelezers.length} {p.meelezers.length === 1 ? "meelezer" : "meelezers"}
                        </span>
                      )}
                    </td>
                    <td style={cel}>{standTekst(p)}</td>
                    <td style={{ ...cel, textAlign: "right", whiteSpace: "nowrap" }}>
                      {p.gestart ? (
                        <>
                          <Knop onClick={() => setInstellen(sleutel(p))} aria-label={`Instelling van ${naam(p)}`}>
                            Instelling
                          </Knop>{" "}
                          <Knop variant="primair" onClick={() => openPlan(p)} aria-label={`Open plan ${naam(p)}`}>
                            Open plan
                          </Knop>
                        </>
                      ) : (
                        <Knop variant="primair" onClick={() => setInstellen(sleutel(p))} aria-label={`Instellen ${naam(p)}`}>
                          Instellen
                        </Knop>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        <section aria-label={`Wijzigingen in ${gekozen.naam}`}>
          <div style={sectionLabel}>Wijzigingen in {naam(gekozen)}</div>
          {gekozen.wijzigingen.length ? (
            <ul style={{ margin: 0, paddingLeft: 16, display: "grid", gap: 3 }}>
              {gekozen.wijzigingen.map((r, i) => (
                <li key={`${r.wanneer}-${i}`} style={{ fontSize: size.meta, color: color.body }}>
                  <span style={{ color: color.faint }}>{tijd(r.wanneer)}</span> ·{" "}
                  <span style={{ fontWeight: weight.medium }}>{r.naam}</span> · {PARAGRAAF_WOORD(r.paragraaf)} · {r.wat}
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ margin: 0, fontSize: size.meta, color: color.soft }}>Nog geen wijzigingen.</p>
          )}
          <p style={{ margin: "8px 0 0", fontSize: size.meta }}>
            <button
              type="button"
              onClick={() => setAlles((a) => !a)}
              aria-expanded={alles}
              style={{ all: "unset", cursor: "pointer", color: color.accentBlue, textDecoration: "underline" }}
            >
              {alles ? "Verberg alle wijzigingen" : "Alle wijzigingen"}
            </button>
          </p>
        </section>

        {alles && <PlannenStatus />}

        {open && (
          <Instellen
            key={sleutel(open)}
            plan={open}
            gebruikers={res.data.gebruikers}
            onSluit={() => setInstellen(null)}
            onGestart={() => {
              setInstellen(null);
              res.reload();
            }}
          />
        )}
      </div>
    </PlatformStijl>
  );
}

const kop = { padding: "10px 14px", fontWeight: weight.medium } as const;
const cel = { padding: "10px 14px", verticalAlign: "top" } as const;

// ── het venster "Instellen" ──────────────────────────────────────────────────

/** Wat er onder elke keuze staat (keuzekaarten in plaats van rondjes, owner 04-11). */
const INVULLEN_UITLEG: Record<PlanInvullen, string> = {
  am_bp: "Allebei vullen in. De uitslag zet jullie naast elkaar.",
  am: "Alleen de AM vult in. Anderen kunnen meelezen.",
  bp: "Alleen de BP vult in. Anderen kunnen meelezen.",
};

/** De rol zoals Gebruikers hem noemt (config/rollen.yaml: accountmanagement, llo). */
const ROL_LABEL = { am: "Accountmanagement", bp: "Opleidingsmanager (BP)" } as const;
const ROL_WOORD = { am: "accountmanagement", bp: "opleidingsmanager (BP)" } as const;

const alsPersoon = (p: PlanPersoon, sub?: string): Persoon => ({ id: p.gebruiker, naam: p.naam, email: p.email, sub: sub ?? p.email });

function Instellen({
  plan,
  gebruikers,
  onSluit,
  onGestart,
}: {
  plan: PlanInstelling;
  gebruikers: readonly PlanPersoon[];
  onSluit: () => void;
  onGestart: () => void;
}) {
  const vast = plan.gestart;
  const [invullen, setInvullen] = useState<PlanInvullen | null>(plan.invullen);
  const [am, setAm] = useState<string>(plan.am?.gebruiker ?? "");
  const [bp, setBp] = useState<string>(plan.bp?.gebruiker ?? "");
  const [lezers, setLezers] = useState<PlanPersoon[]>(plan.meelezers);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const metAm = invullen === "am_bp" || invullen === "am";
  const metBp = invullen === "am_bp" || invullen === "bp";
  const klaar = invullen !== null && (!metAm || !!am) && (!metBp || !!bp);
  const gekozen = new Set([am, bp, ...lezers.map((l) => l.gebruiker)]);
  const kandidaten = gebruikers.filter((g) => !gekozen.has(g.gebruiker));

  const start = async () => {
    if (!invullen || !klaar) return;
    setBezig(true);
    setFout(null);
    try {
      await api.startPlan(plan.propositie, plan.jaar, {
        invullen,
        am: metAm ? am : null,
        bp: metBp ? bp : null,
        meelezers: lezers.map((l) => l.gebruiker),
      });
      onGestart();
    } catch (e) {
      setFout(e instanceof ApiError ? e.message || e.reason : "Starten is niet gelukt.");
    } finally {
      setBezig(false);
    }
  };

  /** Het personenveld van één stoel (AM of BP). */
  const stoel = (rol: "am" | "bp", waarde: string, zet: (id: string) => void) => {
    const sub = `${ROL_LABEL[rol]} · ${plan.naam}`;
    const lijst = plan.kandidaten[rol].map((k) => alsPersoon(k, sub));
    // Na de start de vastgelegde persoon; ervoor wie er net gekozen is.
    const nu = vast ? plan[rol] : ([...plan.kandidaten[rol], plan[rol]].find((k) => k && k.gebruiker === waarde) ?? null);
    return (
      <div className="s-stoel">
        <span className="s-lbl">{rol === "am" ? "AM" : "BP"}</span>
        <PersoonVeld
          label={rol === "am" ? "AM" : "BP"}
          personen={lijst}
          gekozen={nu ? alsPersoon(nu, sub) : null}
          onKies={(p) => zet(p.id)}
          onWissel={() => zet("")}
          vast={vast}
          leeg={
            <>
              <span className="s-notitie__kop">
                Nog niemand met de rol {ROL_WOORD[rol]} bij {plan.naam}.
              </span>
              Nodig iemand uit bij Gebruikers en engagement, met die rol en {plan.naam} als propositie.
            </>
          }
        />
      </div>
    );
  };

  return (
    <Dialoog open onSluit={onSluit} titel={`Plan instellen · ${plan.naam} ${plan.jaar}`}>
      <div className="s-formulier">
        <KeuzeKaarten
          label="Wie vult het plan in?"
          naam="invullen"
          waarde={invullen}
          onKies={setInvullen}
          vast={vast}
          opties={(Object.keys(INVULLEN_WOORD) as PlanInvullen[]).map((k) => ({
            waarde: k,
            titel: INVULLEN_WOORD[k],
            uitleg: INVULLEN_UITLEG[k],
          }))}
        />

        {(metAm || metBp) && (
          <div className="s-stoelen">
            {metAm && stoel("am", am, setAm)}
            {metBp && stoel("bp", bp, setBp)}
          </div>
        )}

        {(invullen === "am" || invullen === "bp") && <Notitie>{EEN_PERSOON}</Notitie>}

        <div className="s-veldblok">
          <span className="s-lbl">Meelezen (alleen lezen)</span>
          {lezers.length ? (
            <ul className="s-persoonchips" aria-label="Meelezers">
              {lezers.map((l) => (
                <li key={l.gebruiker}>
                  <PersoonChip
                    persoon={alsPersoon(l)}
                    onWeg={vast ? undefined : () => setLezers((x) => x.filter((y) => y.gebruiker !== l.gebruiker))}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <span className="s-veldblok__uitleg">Nog niemand.</span>
          )}
          {!vast && (
            <PersoonVeld
              label="Meelezer toevoegen"
              placeholder="Zoek een collega op naam of e-mail"
              personen={kandidaten.map((g) => alsPersoon(g))}
              onKies={(p) => {
                const g = gebruikers.find((x) => x.gebruiker === p.id);
                if (g) setLezers((x) => [...x, g]);
              }}
              leeg={gebruikers.length ? "Iedereen met een account staat er al bij." : "Er is nog niemand met een account."}
            />
          )}
        </div>

        {vast && <Notitie>Na de start staat dit vast tot het plan af is.</Notitie>}
        {fout && <Foutmelding>{fout}</Foutmelding>}

        <div className="s-formulier__voet">
          {vast ? (
            <Knop variant="zacht" onClick={onSluit}>
              Sluiten
            </Knop>
          ) : (
            <>
              <Knop onClick={onSluit}>Annuleren</Knop>
              <Knop variant="primair" disabled={!klaar || bezig} onClick={() => void start()}>
                {bezig ? "Bezig…" : "Start plan"}
              </Knop>
            </>
          )}
        </div>
      </div>
    </Dialoog>
  );
}
